"use client";

import { useState } from "react";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { PortfolioCard, type PortfolioItem } from "./portfolio-card";

export function PortfolioGrid({
  items,
  locale,
  featuredItem,
}: {
  items: PortfolioItem[];
  locale: string;
  featuredItem: PortfolioItem;
}) {
  const t = useTranslations("website.portfolio");
  const [activeFilter, setActiveFilter] = useState("all");

  const filterTabs = [
    { key: "all", label: t("all") },
    { key: "cybersecurity", label: t("cybersecurity") },
    { key: "education", label: t("education") },
    { key: "software", label: t("software") },
  ];

  const filteredItems =
    activeFilter === "all"
      ? items
      : items.filter((item) => item.service_line === activeFilter);

  const featuredTitle =
    locale === "ar"
      ? featuredItem.title_ar || featuredItem.title_en
      : featuredItem.title_en;
  const featuredSummary =
    locale === "ar"
      ? featuredItem.summary_ar || featuredItem.summary_en
      : featuredItem.summary_en;

  return (
    <div>
      {/* Featured hero */}
      <section className="relative overflow-hidden rounded-2xl border border-(--fm-border) bg-(--fm-surface) mb-12">
        <div className="grid md:grid-cols-2">
          {/* Image */}
          <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[360px]">
            {featuredItem.cover_image_url ? (
              <Image
                src={featuredItem.cover_image_url}
                alt={featuredTitle}
                fill
                className="object-cover"
                priority
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-(--fm-accent)/20 to-(--fm-accent)/5" />
            )}
          </div>

          {/* Content */}
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <span className="inline-block w-fit px-3 py-1 text-xs font-medium rounded-full bg-(--fm-accent)/10 text-(--fm-accent) mb-4">
              {featuredItem.service_line === "cybersecurity" ||
              featuredItem.service_line === "education" ||
              featuredItem.service_line === "software"
                ? t(featuredItem.service_line)
                : featuredItem.service_line}
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-(--fm-text-primary) mb-3">
              {featuredTitle}
            </h2>
            {featuredItem.client_name && (
              <p className="text-sm text-(--fm-text-secondary) mb-3">
                {featuredItem.client_name}
              </p>
            )}
            {featuredSummary && (
              <p className="text-(--fm-text-secondary) leading-relaxed mb-6 line-clamp-3">
                {featuredSummary}
              </p>
            )}
            <Link
              href={`/portfolio/${featuredItem.slug}`}
              className="inline-flex items-center gap-1 text-(--fm-accent) font-medium hover:underline"
            >
              {t("viewCaseStudy")}
            </Link>
          </div>
        </div>
      </section>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-8 flex-wrap">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
              activeFilter === tab.key
                ? "bg-(--fm-accent) text-(--fm-background)"
                : "bg-(--fm-surface) text-(--fm-text-secondary) border border-(--fm-border) hover:border-(--fm-accent)/40"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
          >
            <PortfolioCard item={item} locale={locale} />
          </motion.div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <p className="text-center text-(--fm-text-secondary) py-12">
          {t("empty")}
        </p>
      )}
    </div>
  );
}
